import { ButtonRadio } from '@kula-ai/components';
import { isThemePreference } from './colorMode.utils';
import { THEME_TOGGLE_OPTIONS, type ThemeOption } from './themeOptions';
import { useThemePreference } from './useThemePreference';

// Joined icon tabs (Figma "Radio Tab"): kula ButtonRadio has no segmented
// variant, so the group is drawn here — one outer border, square inner edges,
// and the checked tab in primary-200 with a brand divider.
const tabClassName = (index: number, length: number, labelled: boolean) =>
  [
    labelled
      ? // Icon + label: 6px 12px padding, 4px gap, 20px text, so 32px tall.
        '!h-8 !min-h-0 !min-w-0 !px-3 !py-0 !gap-1'
      : // Icon only: 6px padding round a 16px icon, so 28px tall (40px
        // wide) — it sits inside the 44px menu row like the text rows do.
        '!h-7 !min-h-0 !w-10 !min-w-0 !px-0 !py-0',
    '!rounded-none',
    index === 0 && '!rounded-l-md',
    index === length - 1 && '!rounded-r-md',
    index > 0 && '-ml-px',
    'data-[state=checked]:!bg-none data-[state=checked]:!bg-primary-200 data-[state=checked]:!text-primary-600 data-[state=checked]:!border-primary-500',
  ]
    .filter(Boolean)
    .join(' ');

interface ThemeToggleProps {
  /** Tabs to show, in order. Defaults to the avatar menu's. */
  options?: ThemeOption[];
  /** Show each tab's label next to its icon (Profile settings). */
  labelled?: boolean;
}

/**
 * The theme picker: System, Light and Dark as joined tabs — icon-only in the
 * avatar menu, icon + label in Profile settings. System follows the OS
 * (`prefers-color-scheme`).
 */
export const ThemeToggle = ({
  options = THEME_TOGGLE_OPTIONS,
  labelled = false,
}: ThemeToggleProps) => {
  const { preference, setPreference } = useThemePreference();

  return (
    <div
      role="radiogroup"
      aria-label="Theme"
      className="flex"
      data-test-id="theme-toggle"
    >
      {options.map(({ value, label, icon: Icon }, index) => (
        <ButtonRadio
          key={value}
          name="theme"
          value={value}
          checked={preference === value}
          onChange={(next) => {
            if (isThemePreference(next)) setPreference(next);
          }}
          variant="quaternary"
          aria-label={labelled ? undefined : `${label} theme`}
          // A native hint: a kula Tooltip trigger (asChild) overwrote
          // the radio's data-state and swallowed its click.
          title={labelled ? undefined : label}
          data-test-id={`theme-toggle-${value}`}
          className={tabClassName(index, options.length, labelled)}
        >
          <Icon size={16} />
          {labelled && label}
        </ButtonRadio>
      ))}
    </div>
  );
};
